/**
 * Modifica condizioni economiche post-conferma
 * (PATCH /richieste/{id}/condizioni-economiche): tariffa e/o trasporto.
 *
 * Tre fasi:
 *  - input:    tariffa e stato trasporto precompilati dai valori correnti;
 *              si invia SOLO ciò che cambia (payload per blocco, il no-op per
 *              campo resta comunque al backend). Attivando il trasporto la
 *              modale dichiara la semantica PER-CONFERIMENTO: il costo vale
 *              un viaggio per ogni conferimento fatturato, non una tantum —
 *              il preventivo stimava costo × viaggi, la fattura no;
 *  - conferma: il backend ha risposto 409 `conferma_modifica_condizioni_richiesta`
 *              (preventivo firmato vivo) → si mostrano i SOLI blocchi che
 *              cambiano (attuale → nuovo) + il riferimento al preventivo e si
 *              rinvia il payload RIFIUTATO, congelato al momento del 409, col
 *              flag (mai ri-derivato dallo stato live: vedi ConfermaPending).
 *              Ogni fase ha una `key` propria sul Modal → remount al cambio
 *              fase, niente DOM riusato tra fasi quasi identiche;
 *  - esito:    la risposta elenca bozze aperte con conferimenti dell'ordine —
 *              ricalcolano live, i totali cambiano subito. Senza bozze la
 *              modale si chiude direttamente.
 * L'autorità resta il backend: qui solo il canale della conferma.
 */
import { useState } from 'react';
import { AlertTriangle, Info, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import {
  modificaCondizioniEconomiche,
  type ModificaCondizioniPayload,
  type RichiestaDetail,
} from '../../api/richieste';
import type { FatturaRiferimento } from '../../lib/modificaFiscale';
import {
  parseConfermaCondizioni,
  type ConfermaModificaCondizioniDetail,
  type CondizioniTrasportoStato,
} from '../../lib/modificaCondizioni';
import { apiErrorMessage } from '../../lib/apiError';

/** Fase conferma: il 409 strutturato + il payload ESATTO che è stato rifiutato.
 * Il retry rispedisce QUESTO payload col flag — mai una ri-derivazione dallo
 * stato live: se props o input cambiassero tra il 409 e la conferma, la
 * ri-derivazione potrebbe produrre un payload diverso (perfino vuoto → 422). */
interface ConfermaPending {
  detail: ConfermaModificaCondizioniDetail;
  payload: ModificaCondizioniPayload;
}

function formatEurKg(v: number | null): string {
  if (v == null) return '—';
  return `${v.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 5 })} €/kg`;
}

function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

interface Props {
  richiestaId: string;
  tariffaAttuale: number | null;
  /** Da RichiestaDetail: cliente_porta_materiale (invertito) + costo per viaggio. */
  trasportoAbilitatoAttuale: boolean;
  costoTrasportoAttuale: number | null;
  onClose: () => void;
  /** Chiamata al successo con il dettaglio aggiornato (la modale può restare aperta per l'esito). */
  onSaved: (updated: RichiestaDetail) => void;
}

export default function ModificaCondizioniModal({
  richiestaId,
  tariffaAttuale,
  trasportoAbilitatoAttuale,
  costoTrasportoAttuale,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [tariffa, setTariffa] = useState(tariffaAttuale != null ? String(tariffaAttuale) : '');
  const [trasportoAbilitato, setTrasportoAbilitato] = useState(trasportoAbilitatoAttuale);
  const [costoTrasporto, setCostoTrasporto] = useState(
    costoTrasportoAttuale != null ? String(costoTrasportoAttuale) : '',
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conferma, setConferma] = useState<ConfermaPending | null>(null);
  const [bozzeEsito, setBozzeEsito] = useState<FatturaRiferimento[] | null>(null);

  // ── Diff client-side: si invia solo il blocco che cambia ──────────────────
  const parsedTariffa = parseFloat(tariffa.replace(',', '.'));
  const tariffaValida = !Number.isNaN(parsedTariffa) && parsedTariffa >= 0;
  const tariffaCambia =
    tariffaValida && (tariffaAttuale == null || parsedTariffa !== tariffaAttuale);

  const parsedCosto = parseFloat(costoTrasporto.replace(',', '.'));
  const costoValido = !Number.isNaN(parsedCosto) && parsedCosto > 0;
  const trasportoCambia =
    trasportoAbilitato !== trasportoAbilitatoAttuale ||
    (trasportoAbilitato && costoValido && parsedCosto !== costoTrasportoAttuale);
  const trasportoValido = !trasportoAbilitato || costoValido;

  const inputValido =
    (tariffaCambia || trasportoCambia) &&
    (!tariffaCambia || tariffaValida) &&
    (!trasportoCambia || trasportoValido);

  function statoTrasportoLabel(s: CondizioniTrasportoStato): string {
    if (!s.abilitato) return t('richieste.modifica_condizioni.trasporto_disabilitato');
    if (s.costo_per_viaggio == null) return t('richieste.modifica_condizioni.trasporto_senza_costo');
    return t('richieste.modifica_condizioni.trasporto_costo_viaggio', {
      costo: s.costo_per_viaggio.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    });
  }

  /** Payload della fase input, derivato dagli input correnti (solo i blocchi che cambiano). */
  function buildPayload(): ModificaCondizioniPayload {
    return {
      ...(tariffaCambia ? { tariffa_unitaria_eur_kg: parsedTariffa } : {}),
      ...(trasportoCambia
        ? {
            trasporto: trasportoAbilitato
              ? { abilita: true, costo_per_viaggio: parsedCosto }
              : { abilita: false },
          }
        : {}),
    };
  }

  async function invia(payload: ModificaCondizioniPayload) {
    setIsSaving(true);
    setError(null);
    try {
      const updated = await modificaCondizioniEconomiche(richiestaId, payload);
      onSaved(updated);
      const bozze = updated.avviso_bozze_aperte ?? [];
      if (bozze.length > 0) {
        setConferma(null);
        setBozzeEsito(bozze);
      } else {
        onClose();
      }
    } catch (err: unknown) {
      const detail = parseConfermaCondizioni(err);
      if (detail) {
        // Congela il payload rifiutato: la conferma rispedirà ESATTAMENTE
        // questo + il flag (vedi ConfermaPending).
        setConferma({ detail, payload });
        return;
      }
      setError(apiErrorMessage(err, t('richieste.modifica_condizioni.errore')));
      setConferma(null);
    } finally {
      setIsSaving(false);
    }
  }

  // ── Fase ESITO: bozze aperte che ricalcolano live ─────────────────────────
  if (bozzeEsito) {
    return (
      <Modal
        key="fase-esito"
        title={t('richieste.modifica_condizioni.esito_titolo')}
        onClose={onClose}
        maxWidth="max-w-xl"
        testid="modifica-condizioni-esito-modal"
        footer={
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-[#5C6F59] transition-colors"
          >
            {t('common.azioni.chiudi')}
          </button>
        }
      >
        <div className="flex gap-2.5 text-sm text-ink-dark">
          <Info size={16} className="text-accent-info flex-shrink-0 mt-0.5" />
          <p>{t('richieste.modifica_condizioni.esito_bozze', { count: bozzeEsito.length })}</p>
        </div>
        <ul className="border border-stone-mid rounded-md divide-y divide-stone-mid max-h-52 overflow-y-auto">
          {bozzeEsito.map((b) => (
            <li key={b.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="font-mono text-ink-dark flex-1">{b.numero ?? '—'}</span>
              <span className="text-xs text-ink-mid capitalize">{b.stato}</span>
            </li>
          ))}
        </ul>
      </Modal>
    );
  }

  // ── Fase CONFERMA: preventivo firmato vivo (409, soli blocchi che cambiano) ─
  // key ≠ fase input: il pannello viene RIMONTATO al cambio fase. Senza key,
  // React riusa lo stesso DOM (stessi bottoni, spostati dal max-w diverso): un
  // secondo click arrivato subito dopo il 409 atterrerebbe su un bottone
  // riciclato — tipicamente «Annulla», che chiude la conferma IN SILENZIO e
  // riporta alla fase input quasi identica, il cui submit riparte SENZA flag.
  if (conferma) {
    return (
      <Modal
        key="fase-conferma"
        title={t('richieste.modifica_condizioni.conferma_titolo')}
        subtitle={t('richieste.modifica_condizioni.conferma_sottotitolo')}
        onClose={() => setConferma(null)}
        maxWidth="max-w-xl"
        testid="conferma-modifica-condizioni-modal"
        footer={
          <>
            <button
              type="button"
              onClick={() => setConferma(null)}
              disabled={isSaving}
              className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors disabled:opacity-60"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="button"
              onClick={() => void invia({ ...conferma.payload, conferma_modifica_condizioni: true })}
              disabled={isSaving}
              data-testid="conferma-modifica-condizioni-submit"
              className="px-3 py-1.5 text-xs font-medium text-white bg-accent-danger rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {isSaving ? t('common.azioni.salvataggio_in_corso') : t('richieste.modifica_condizioni.conferma')}
            </button>
          </>
        }
      >
        <div className="flex gap-2.5 text-sm text-ink-dark">
          <AlertTriangle size={16} className="text-accent-warning flex-shrink-0 mt-0.5" />
          <p>{t('richieste.modifica_condizioni.avvertenza')}</p>
        </div>
        {conferma.detail.tariffa && (
          <div className="flex items-center gap-3 text-sm">
            <div>
              <p className="text-xs text-ink-soft mb-0.5">{t('richieste.modifica_condizioni.tariffa_attuale')}</p>
              <p className="font-medium text-ink-dark">{formatEurKg(conferma.detail.tariffa.attuale)}</p>
            </div>
            <span className="text-ink-soft">→</span>
            <div>
              <p className="text-xs text-ink-soft mb-0.5">{t('richieste.modifica_condizioni.tariffa_nuova')}</p>
              <p className="font-medium text-ink-dark">{formatEurKg(conferma.detail.tariffa.nuova)}</p>
            </div>
          </div>
        )}
        {conferma.detail.trasporto && (
          <>
            <div className="flex items-center gap-3 text-sm">
              <div>
                <p className="text-xs text-ink-soft mb-0.5">{t('richieste.modifica_condizioni.trasporto_attuale')}</p>
                <p className="font-medium text-ink-dark">{statoTrasportoLabel(conferma.detail.trasporto.attuale)}</p>
              </div>
              <span className="text-ink-soft">→</span>
              <div>
                <p className="text-xs text-ink-soft mb-0.5">{t('richieste.modifica_condizioni.trasporto_nuovo')}</p>
                <p className="font-medium text-ink-dark">{statoTrasportoLabel(conferma.detail.trasporto.nuovo)}</p>
              </div>
            </div>
            {conferma.detail.trasporto.nuovo.abilitato && (
              <p className="text-xs text-ink-mid">
                {t('richieste.modifica_condizioni.semantica_per_conferimento')}
              </p>
            )}
          </>
        )}
        <p className="text-xs text-ink-soft">
          {t('richieste.modifica_condizioni.preventivo_firmato_il', {
            data: formatDateIT(
              conferma.detail.preventivo?.confermato_at
                ?? conferma.detail.preventivo?.firmato_caricato_at ?? null,
            ),
          })}
        </p>
        {error && <p className="text-xs text-accent-danger">{error}</p>}
      </Modal>
    );
  }

  // ── Fase INPUT ────────────────────────────────────────────────────────────
  return (
    <Modal
      key="fase-input"
      title={t('richieste.modifica_condizioni.titolo')}
      subtitle={t('richieste.modifica_condizioni.sottotitolo')}
      onClose={onClose}
      maxWidth="max-w-md"
      testid="modifica-condizioni-modal"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-white transition-colors disabled:opacity-60"
          >
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={() => void invia(buildPayload())}
            disabled={isSaving || !inputValido}
            data-testid="modifica-condizioni-submit"
            className="px-3 py-1.5 text-xs font-medium text-white bg-sage rounded-md hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
          >
            {isSaving ? t('common.azioni.salvataggio_in_corso') : t('common.azioni.salva')}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-mid">{t('richieste.modifica_condizioni.spiegazione')}</p>
      <div>
        <label htmlFor="condizioni-tariffa" className="block text-xs text-ink-soft mb-1">
          {t('richieste.modifica_condizioni.label_tariffa')}
        </label>
        <input
          id="condizioni-tariffa"
          type="text"
          inputMode="decimal"
          value={tariffa}
          onChange={(e) => setTariffa(e.target.value)}
          data-testid="modifica-condizioni-tariffa-input"
          className="w-full px-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-1 focus:ring-sage"
        />
        <p className="text-[11px] text-ink-faint mt-1">
          {t('richieste.modifica_condizioni.attuale_hint', { tariffa: formatEurKg(tariffaAttuale) })}
        </p>
      </div>
      <div className="border-t border-stone-mid pt-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-ink-dark cursor-pointer">
          <input
            type="checkbox"
            checked={trasportoAbilitato}
            onChange={(e) => setTrasportoAbilitato(e.target.checked)}
            data-testid="modifica-condizioni-trasporto-toggle"
            className="rounded border-stone-border text-sage focus:ring-sage"
          />
          <Truck size={14} className="text-ink-soft" />
          {t('richieste.modifica_condizioni.label_trasporto_abilita')}
        </label>
        {trasportoAbilitato && (
          <div>
            <label htmlFor="condizioni-costo-trasporto" className="block text-xs text-ink-soft mb-1">
              {t('richieste.modifica_condizioni.label_costo')}
            </label>
            <input
              id="condizioni-costo-trasporto"
              type="text"
              inputMode="decimal"
              value={costoTrasporto}
              onChange={(e) => setCostoTrasporto(e.target.value)}
              data-testid="modifica-condizioni-costo-input"
              className="w-full px-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-1 focus:ring-sage"
            />
            {/* Semantica PER-CONFERIMENTO: senza questa frase l'operatore scopre
                in fattura una riga trasporto per ogni conferimento che non si
                aspettava (il preventivo stimava costo × viaggi, la fattura no). */}
            <div className="flex gap-2 mt-2 text-xs text-ink-mid">
              <Info size={14} className="text-accent-info flex-shrink-0 mt-0.5" />
              <p>{t('richieste.modifica_condizioni.semantica_per_conferimento')}</p>
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-accent-danger">{error}</p>}
    </Modal>
  );
}
